
import { useState, useEffect, useRef } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { User } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

// Fix case sensitivity by using correct file extensions (JPG instead of jpg)
const profileImages = [
  "/profile-images/profile_1.JPG",
  "/profile-images/profile_2.JPG",
  "/profile-images/profile_4.JPG",
];

const ProfileImageGallery = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [touchStartX, setTouchStartX] = useState(0);
  const flipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useIsMobile();

  // Auto-flip timer effect
  useEffect(() => {
    // Start the timer when component is visible and not currently flipping
    flipTimerRef.current = setInterval(() => {
      handleImageFlip();
    }, 5000);

    // Cleanup function to clear the timer
    return () => {
      if (flipTimerRef.current) {
        clearInterval(flipTimerRef.current);
      }
    };
  }, [isFlipping]);

  const handleImageFlip = () => {
    if (isFlipping || profileImages.length <= 1) return;
    
    setIsFlipping(true);
    
    setTimeout(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === profileImages.length - 1 ? 0 : prevIndex + 1
      );
      
      setTimeout(() => {
        setIsFlipping(false);
      }, 500); // Half of the flip animation duration
    }, 500);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchEndX - touchStartX;
    
    // If swipe was significant enough (more than 50px)
    if (Math.abs(diffX) > 50) {
      // Reset timer when user manually swipes
      if (flipTimerRef.current) {
        clearInterval(flipTimerRef.current);
        flipTimerRef.current = setInterval(() => {
          handleImageFlip();
        }, 5000);
      }
      
      // Swipe right - next image
      if (diffX > 0) {
        handleImageFlip();
      }
      // Swipe left - previous image (optional)
      else {
        if (isFlipping) return;
        
        setIsFlipping(true);
        
        setTimeout(() => {
          setCurrentImageIndex((prevIndex) => 
            prevIndex === 0 ? profileImages.length - 1 : prevIndex - 1
          );
          
          setTimeout(() => {
            setIsFlipping(false);
          }, 500);
        }, 500);
      }
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };
  
  const handleImageError = () => {
    console.error('Failed to load image:', profileImages[currentImageIndex]);
    setImageError(true);
    
    // Try with absolute URL or various prefixes as fallback
    const attemptLoadWithPrefix = () => {
      const baseUrl = window.location.origin;
      const imagePath = profileImages[currentImageIndex];
      
      // Try with different combinations to handle various server configurations
      const possiblePaths = [
        `${baseUrl}${imagePath}`,                    // Absolute with origin
        `${baseUrl}/public${imagePath}`,             // With /public prefix
        imagePath.replace('/profile-images/', '/public/profile-images/'), // Public in path
        imagePath                                    // Original path
      ];
      
      console.log('Attempting to load image with fallback paths:', possiblePaths);
      
      // Create an array of promises for each path attempt
      const loadAttempts = possiblePaths.map((path) => {
        return new Promise<{success: boolean, path: string}>((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ success: true, path });
          img.onerror = () => resolve({ success: false, path });
          img.src = path;
        });
      });
      
      // Try each path until one works
      Promise.all(loadAttempts).then((results) => {
        const successResult = results.find(result => result.success);
        if (successResult) {
          console.log('Successfully loaded image with path:', successResult.path);
          setImageError(false);
          // Force re-render but don't change the index
          setCurrentImageIndex(prev => prev);
        } else {
          console.error('All image loading attempts failed');
        }
      });
    };
    
    // Attempt loading with different paths
    attemptLoadWithPrefix();
  };
  
  // Get the correct image path with potential fallbacks
  const getImagePath = () => {
    // Start with the basic path
    return profileImages[currentImageIndex];
  };
  
  return (
    <div>
      <div 
        className={`relative cursor-pointer ${isFlipping ? 'profile-flip' : ''}`} 
        onClick={isMobile ? undefined : handleImageFlip}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
        <Avatar className="h-40 w-40 md:h-48 md:w-48 border-4 border-primary/20 shadow-lg hover:scale-105 transition-transform duration-300">
          {imageError ? (
            <AvatarFallback className="bg-secondary/50 text-foreground">
              <User className="h-12 w-12" />
            </AvatarFallback>
          ) : (
            <>
              <AvatarImage 
                src={getImagePath()} 
                alt="Profile photo" 
                className="object-cover object-center"
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
              <AvatarFallback className="bg-secondary/50 text-foreground">
                <User className="h-12 w-12" />
              </AvatarFallback>
            </>
          )}
        </Avatar>
      </div>
      
      {profileImages.length > 1 && (
        <div className="image-indicators mt-2">
          {profileImages.map((_, index) => (
            <div 
              key={index} 
              className={`indicator-dot ${currentImageIndex === index ? 'active' : ''}`}
            ></div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfileImageGallery;
